import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 576);
  React.useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 576);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return isMobile;
}

function SignedDocsList() {
  const [signedDocs, setSignedDocs] = useState([]);
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(() => {
    const saved = localStorage.getItem('signedDocsPageSize');
    return saved ? parseInt(saved) : 20;
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [searchText, setSearchText] = useState('');
  const [downloadingAll, setDownloadingAll] = useState(false);
  const { id } = useParams();
  const { authFetch } = useAuth();
  const isMobile = useIsMobile();

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch template details
        const templateResponse = await authFetch(`/api/v1/template-docs/${id}`);
        if (!templateResponse.ok) {
          throw new Error('Failed to fetch template');
        }
        const templateData = await templateResponse.json();
        setTemplate(templateData);

        // Fetch signed documents with pagination
        let listUrl = `/api/v1/signed-docs?template_id=${id}&page=${currentPage}&limit=${pageSize}`;
        if (searchTerm && searchTerm.trim() !== '') {
          listUrl += `&contragent_name=${encodeURIComponent(searchTerm.trim())}`;
        }
        const signedDocsResponse = await authFetch(listUrl);
        if (!signedDocsResponse.ok) {
          throw new Error('Failed to fetch signed documents');
        }
        const signedDocsData = await signedDocsResponse.json();
        setSignedDocs(signedDocsData.data);
        setPagination(signedDocsData.pagination);
      } catch (error) {
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, authFetch, currentPage, pageSize, searchTerm]);

  const handleDownload = async (doc) => {
    try {
      // Build the URL with conditional format parameter
      let downloadUrl = `/api/v1/signed-docs/${doc.id}/download`;

      let extension = 'pdf';
      
      // If readable_name is empty, add format=docx parameter
      if (doc.readable_name && doc.readable_name.trim() !== '') {
        downloadUrl += '?format=docx';
        extension = 'docx';
      }
      
      const response = await authFetch(downloadUrl);
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; 
        const downloadFilename = `document-${doc.id}.${extension}`;
        a.download = downloadFilename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        setError('Failed to download file');
      }
    } catch (error) {
      setError('Error downloading file: ' + error.message);
    }
  };

  const handleDownloadAll = async () => {
    setDownloadingAll(true);
    try {
      const downloadUrl = `/api/v1/signed-docs/template/${id}/download-zip?format=docx`;
      const response = await authFetch(downloadUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const templateName = template?.name || 'template';
        a.download = `${templateName}-all-documents.zip`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        setError('Не удалось скачать архив');
      }
    } catch (error) {
      setError('Ошибка при скачивании архива: ' + error.message);
    } finally {
      setDownloadingAll(false);
    }
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    setLoading(true);
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setCurrentPage(1); // Reset to first page when changing page size
    localStorage.setItem('signedDocsPageSize', newPageSize.toString());
    setLoading(true);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    setSearchTerm(searchText.trim());
    setLoading(true);
  };

  const handleDeleteTokenGroup = async (tokenDocs) => {
    if (!window.confirm('Вы уверены, что хотите удалить все документы этой группы?')) {
      return;
    }
    try {
      const deleteResults = await Promise.all(
        tokenDocs.map((d) => authFetch(`/api/v1/signed-docs/${d.id}`, { method: 'DELETE' }))
      );

      // Determine which deletions actually succeeded
      const successfulIds = new Set(
        tokenDocs
          .map((d, i) => (deleteResults[i] && deleteResults[i].ok ? d.id : null))
          .filter((id) => id !== null)
      );

      if (successfulIds.size === 0) {
        setError('Не удалось удалить документы группы');
        return;
      }

      // Optimistically update local state without refetching
      setSignedDocs((prev) => prev.filter((d) => !successfulIds.has(d.id)));
      setPagination((prev) =>
        prev
          ? {
              ...prev,
              total: Math.max(0, (prev.total || 0) - successfulIds.size),
            }
          : prev
      );

      // If some deletions failed, inform the user but keep the successful removals
      const anyFailed = deleteResults.some((r) => !r || !r.ok);
      if (anyFailed) {
        setError('Некоторые документы не удалось удалить');
      }
    } catch (error) {
      setError('Ошибка при удалении группы документов: ' + error.message);
    }
  };

  // Group documents by contragent_name
  const groupedDocs = signedDocs.reduce((groups, doc) => {
    const contragentName = doc.contragent_name;
    if (!groups[contragentName]) {
      groups[contragentName] = [];
    }
    groups[contragentName].push(doc);
    return groups;
  }, {});

  if (loading) {
    return (
      <div className="container mt-4">
        <div className="text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mt-4">
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      </div>
    );
  }

  if (!template) {
    return (
      <div className="container mt-4">
        <div className="alert alert-warning" role="alert">
          Template not found
        </div>
      </div>
    );
  }

  return (
    <div className={`container mt-4${isMobile ? ' p-0' : ''}`}> 
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Подписанные документы: {template?.name}</h2>
        <div className="d-flex align-items-center gap-3">
          <button 
            className="btn btn-primary"
            onClick={handleDownloadAll}
            disabled={!template || !signedDocs || signedDocs.length === 0 || downloadingAll}
          >
            {downloadingAll ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Загрузка...
              </>
            ) : (
              <>
                <i className="fa fa-solid fa-download me-2"></i>
                Скачать все
              </>
            )}
          </button>
          <form className="d-flex align-items-center gap-2" onSubmit={handleSearchSubmit}>
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder="Поиск по контрагенту"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: '16rem' }}
            />
            <button type="submit" className="btn btn-sm btn-primary">Искать</button>
          </form>
          <div className="d-flex align-items-center">
            <label htmlFor="pageSizeSelect" className="me-2 small">Показать:</label>
            <select
              id="pageSizeSelect"
              className="form-select form-select-sm"
              style={{ width: 'auto' }}
              value={pageSize}
              onChange={(e) => handlePageSizeChange(parseInt(e.target.value))}
            >
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
          <Link to={`/template-docs/${id}`} className="btn btn-outline-secondary">
            <i className="bi bi-arrow-left me-2"></i>
            Назад к шаблону
          </Link>
        </div>
      </div>

      {!signedDocs || signedDocs.length === 0 ? (
        <div className="alert alert-info">
          <i className="bi bi-info-circle me-2"></i>
          Нет подписанных документов для этого шаблона
        </div>
      ) : (
        <div>
          {isMobile ? (
            <div>
              {Object.entries(groupedDocs).map(([contragentName, docs]) => (
                <div key={contragentName} className="mb-4">
                  <h4 className="mb-3">Контрагент: {contragentName}</h4>
                  <div className="d-flex flex-column gap-3">
                    {Object.entries(docs.reduce((acc, d) => {
                      const token = d.contragent_token || '—';
                      if (!acc[token]) acc[token] = [];
                      acc[token].push(d);
                      return acc;
                    }, {})).map(([token, tokenDocs]) => {
                      const groupDateMs = Math.min(...tokenDocs.map((d) => new Date(d.created_at).getTime()));
                      const groupDate = new Date(groupDateMs).toLocaleString(undefined, { hour12: false });
                      return (
                        <div key={token} className="mb-3 pb-2 border-bottom">
                          <div className="d-flex align-items-center justify-content-between mb-2">
                            <div className="small text-muted">Дата: {groupDate}</div>
                            <button 
                              className="btn btn-sm btn-outline-danger"
                              title="Удалить группу"
                              onClick={() => handleDeleteTokenGroup(tokenDocs)}
                            >
                              <i className="fa fa-solid fa-trash me-1"></i>
                              Удалить группу
                            </button>
                          </div>
                          <div className="d-flex flex-column gap-2">
                            {tokenDocs.map((doc) => (
                              <div key={doc.id} className="border rounded p-3 bg-light">
                                <div className="small text-muted mb-1">Файл: {doc.readable_name || "Основной документ"}</div>
                                <div className="small text-muted mb-2">Создан: {new Date(doc.created_at).toLocaleString(undefined, { hour12: false })}</div>
                                <div className="btn-group w-100" role="group">
                                  <button 
                                    className="btn btn-sm btn-primary w-100"
                                    onClick={() => handleDownload(doc)}
                                  >
                                    <i className="bi bi-download me-1"></i>
                                    Скачать
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-striped table-sm small align-middle">
                <thead>
                  <tr>
                    <th>Контрагент</th>
                    <th>Документы</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(groupedDocs).map(([contragentName, docs]) => (
                    <tr key={contragentName}>
                      <td className="fw-semibold text-nowrap">{contragentName}</td>
                      <td>
                        {Object.entries(docs.reduce((acc, d) => {
                          const token = d.contragent_token || '—';
                          if (!acc[token]) acc[token] = [];
                          acc[token].push(d);
                          return acc;
                        }, {})).map(([token, tokenDocs]) => {
                          const groupDateMs = Math.min(...tokenDocs.map((d) => new Date(d.created_at).getTime()));
                          const groupDate = new Date(groupDateMs).toLocaleString(undefined, { hour12: false });
                          return (
                            <div key={token} className="mb-3 pb-2 border-bottom">
                              <div className="d-flex align-items-center justify-content-between mb-2">
                                <div className="text-muted small mb-1">Дата: {groupDate}</div>
                                <button 
                                  className="btn btn-sm btn-outline-danger"
                                  title="Удалить группу"
                                  onClick={() => handleDeleteTokenGroup(tokenDocs)}
                                >
                                  <i className="fa fa-solid fa-trash me-1"></i>
                                  Удалить группу
                                </button>
                              </div>
                              <div className="d-flex flex-wrap gap-2">
                                {tokenDocs.map((doc) => (
                                  <div key={doc.id} className="d-inline-flex align-items-center border rounded px-2 py-1 bg-light">
                                    <span className="me-2">
                                      {doc.readable_name || "Основной документ"}
                                    </span>
                                    <button 
                                      className="btn btn-sm btn-primary"
                                      onClick={() => handleDownload(doc)}
                                    >
                                      <i className="fa fa-solid fa-download"></i>
                                    </button>
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        })}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Pagination Controls */}
      {pagination && pagination.total_pages > 1 && (
        <div className="d-flex justify-content-center mt-4">
          <nav aria-label="Pagination">
            <ul className="pagination">
              <li className={`page-item ${!pagination.has_prev ? 'disabled' : ''}`}>
                <button 
                  className="page-link" 
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={!pagination.has_prev}
                >
                  <i className="fa-solid fa-chevron-left"></i>
                </button>
              </li>
              
              {Array.from({ length: Math.min(5, pagination.total_pages) }, (_, i) => {
                let pageNum;
                if (pagination.total_pages <= 5) {
                  pageNum = i + 1;
                } else if (pagination.page <= 3) {
                  pageNum = i + 1;
                } else if (pagination.page >= pagination.total_pages - 2) {
                  pageNum = pagination.total_pages - 4 + i;
                } else {
                  pageNum = pagination.page - 2 + i;
                }
                
                return (
                  <li key={pageNum} className={`page-item ${pageNum === pagination.page ? 'active' : ''}`}>
                    <button 
                      className="page-link" 
                      onClick={() => handlePageChange(pageNum)}
                    >
                      {pageNum}
                    </button>
                  </li>
                );
              })}
              
              <li className={`page-item ${!pagination.has_next ? 'disabled' : ''}`}>
                <button 
                  className="page-link" 
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={!pagination.has_next}
                >
                  <i className="fa-solid fa-chevron-right"></i>
                </button>
              </li>
            </ul>
          </nav>
        </div>
      )}

      {/* Pagination Info */}
      {pagination && (
        <div className="text-center mt-3 text-muted">
          <small>
            Показано {((pagination.page - 1) * pagination.limit) + 1} - {Math.min(pagination.page * pagination.limit, pagination.total)} из {pagination.total} документов
          </small>
        </div>
      )}
    </div>
  );
}

export default SignedDocsList; 